// Beheer → E-mailinstellingen (v18.4 round 3, 3.4) — manager+.
// Eén plek die laat zien welke mail er uitgaat, naar wie, en wat er verstuurd is.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail, Eye, Send, Users, List, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { getAccessToken } from '@/lib/tokenStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
};

const SWITCH_LABELS = {
  dagoverzicht: 'Dagoverzicht checklists (08:00)',
  weekoverzicht: 'Weekoverzicht checklists (ma 07:00)',
  performance_week: 'Performance week (ma 07:30)',
  performance_maand: 'Performance maand (1e 08:00)',
};

const VOORBEELDEN = [
  ['dagoverzicht', 'Dagoverzicht'], ['weekoverzicht', 'Weekoverzicht'],
  ['performance', 'Performance'], ['nieuwe_checklist', 'Nieuwe checklist'],
  ['verlopen', 'Verlopen'], ['escalatie', 'Escalatie'],
  ['ingevuld_notificatie', 'Ingevuld'], ['reminder', 'Herinnering'],
];

const fmt = (d) => d ? new Date(d).toLocaleString('nl-NL', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '-';

const TABS = [
  { key: 'schakelaars', label: 'Schakelaars', icon: Settings },
  { key: 'matrix', label: 'Matrix', icon: List },
  { key: 'wie', label: 'Wie krijgt wat', icon: Users },
  { key: 'log', label: 'Verzendlog', icon: Mail },
];

export default function EmailInstellingen() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('schakelaars');
  const [switches, setSwitches] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [gebruikers, setGebruikers] = useState([]);
  const [gekozen, setGekozen] = useState('');
  const [wie, setWie] = useState(null);
  const [log, setLog] = useState({ rijen: [], totaal: 0 });
  const [logFilter, setLogFilter] = useState({ soort: '', status: '', ontvanger: '' });
  const [skip, setSkip] = useState(0);
  const [voorbeeld, setVoorbeeld] = useState(null);
  // 5.2: wat veranderde er aan het mailgedrag bij de v18.4-update?
  const [migratie, setMigratie] = useState(null);
  const [bezig, setBezig] = useState(false);

  const haal = useCallback(async (pad) => {
    const res = await fetch(`${API}/v2/email${pad}`, { headers: hdrs() });
    if (!res.ok) throw new Error('laden mislukt');
    return res.json();
  }, []);

  useEffect(() => {
    haal('/migratie-rapport').then(setMigratie).catch(() => setMigratie(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    haal('/instellingen').then(setSwitches).catch(() => toast.error('Instellingen laden mislukt'));
    haal('/matrix').then(setMatrix).catch(() => {});
    fetch(`${API}/gebruikers`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : []).then(setGebruikers).catch(() => {});
  }, [haal]);

  // 6.27d/E14f: niet bij elke toetsaanslag; en een verouderd antwoord mag een
  // nieuwer antwoord niet overschrijven.
  const logTellerRef = useRef(0);
  const laadLog = useCallback(async (nieuweSkip = 0, filter = null) => {
    // F16: Vorige/Volgende gebruiken het TOEGEPASTE filter, niet wat er getypt is
    const actief = filter || toegepastRef.current;
    if (filter) toegepastRef.current = filter;
    const params = new URLSearchParams({ skip: String(nieuweSkip), limit: '25' });
    Object.entries(actief).forEach(([k, v]) => { if (v) params.set(k, v); });
    const beurt = ++logTellerRef.current;
    try {
      const data = await haal(`/log?${params.toString()}`);
      if (beurt !== logTellerRef.current) return;
      setLog(data);
      setSkip(nieuweSkip);
    } catch {
      if (beurt === logTellerRef.current) toast.error('Verzendlog laden mislukt');
    }
  }, [haal]);

  useEffect(() => { if (tab === 'log') laadLog(0); }, [tab, laadLog]);

  const toegepastRef = useRef({ soort: '', status: '', ontvanger: '' });

  const zetSwitch = async (naam, waarde) => {
    const nieuw = { ...switches, [naam]: waarde };
    setSwitches(nieuw);
    const res = await fetch(`${API}/v2/email/instellingen`, {
      method: 'PUT', headers: hdrs(), body: JSON.stringify({ [naam]: waarde }),
    });
    if (res.ok) {
      // F14: de auditregel komt uit het antwoord, anders toont hij de vórige wijziging
      const vers = await res.json().catch(() => null);
      if (vers) setSwitches(vers);
      toast.success('Opgeslagen');
    } else {
      setSwitches(switches);                   // terugdraaien
      toast.error('Opslaan mislukt');
    }
  };

  const kiesGebruiker = async (id) => {
    setGekozen(id);
    try { setWie(await haal(`/wie-krijgt-wat/${id}`)); }
    catch { toast.error('Ophalen mislukt'); }
  };

  const testmail = async () => {
    setBezig(true);
    try {
      const res = await fetch(`${API}/v2/email/testmail`, { method: 'POST', headers: hdrs() });
      const data = await res.json().catch(() => ({}));
      const status = data?.resultaat?.status;
      // 6.27c: alleen "verzonden" is succes; anders de echte reden tonen
      if (res.ok && status === 'verzonden') {
        toast.success(`Testmail verzonden naar ${data.naar}`);
      } else {
        toast.error(data?.detail || data?.resultaat?.fout || data?.resultaat?.reden
          || `Testmail niet verzonden (${status || res.status})`);
      }
    } catch {
      toast.error('Testmail niet verzonden — de server is niet bereikbaar');
    } finally { setBezig(false); }
  };

  const toonVoorbeeld = async (soort, gebruikerId) => {
    try { setVoorbeeld(await haal(`/voorbeeld/${soort}${gebruikerId ? `?gebruiker_id=${gebruikerId}` : ''}`)); }
    catch { toast.error('Voorbeeld ophalen mislukt'); }
  };

  return (
    <div className="space-y-5 max-w-6xl mx-auto px-4 sm:px-0" data-testid="email-instellingen-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1f2548] flex items-center gap-2">
            <Mail className="w-6 h-6" /> E-mailinstellingen
          </h1>
          <p className="text-sm text-slate-500">Wat gaat er uit, naar wie, en wat is er verstuurd</p>
        </div>
        <Button variant="outline" onClick={testmail} disabled={bezig} data-testid="testmail-btn">
          {bezig ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
          Testmail naar mijzelf
        </Button>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 overflow-x-auto" data-testid="email-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`email-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-all ${
              tab === t.key ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'schakelaars' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100"
            data-testid="email-switches">
            {Object.entries(SWITCH_LABELS).map(([sleutel, label]) => (
              <div key={sleutel} className="flex items-center justify-between px-5 py-4">
                <span className="text-sm text-slate-700">
                  {label}
                  {switches?.audit?.[sleutel]?.op && (
                    <span className="block text-xs text-slate-400" data-testid={`switch-audit-${sleutel}`}>
                      Gewijzigd door {switches.audit[sleutel].door || 'onbekend'} op{' '}
                      {new Date(switches.audit[sleutel].op).toLocaleString('nl-NL')}
                    </span>
                  )}
                </span>
                <Switch checked={!!switches?.[sleutel]} onCheckedChange={v => zetSwitch(sleutel, v)}
                  aria-label={label}
                  data-testid={`switch-${sleutel}`} />
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm font-medium text-slate-700 mb-3">Voorbeeld bekijken</p>
            <div className="flex flex-wrap gap-2">
              {VOORBEELDEN.map(([sleutel, label]) => (
                <Button key={sleutel} size="sm" variant="outline" onClick={() => toonVoorbeeld(sleutel)}
                  data-testid={`voorbeeld-${sleutel}`}>
                  <Eye className="w-3 h-3 mr-1" /> {label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'matrix' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="email-matrix">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                <th className="py-3 px-4 font-semibold text-slate-700">Template</th>
                {Object.entries(matrix?.labels || {}).map(([k, label]) => (
                  <th key={k} className="py-3 px-3 font-semibold text-slate-700 text-center">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(matrix?.rijen || []).map(rij => (
                <tr key={rij.template_id} className="hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium text-slate-700">{rij.naam}</td>
                  {Object.keys(matrix.labels).map(k => {
                    const cel = rij.triggers[k];
                    return (
                      <td key={k} className="py-2 px-3 text-center">
                        <button onClick={() => navigate(`/beheer/checklist-templates?template=${rij.template_id}#email`)}
                          className="inline-flex flex-col items-center gap-0.5"
                          data-testid={`matrix-${rij.template_id}-${k}`}>
                          <Badge className={cel.actief ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}>
                            {cel.actief ? 'aan' : 'uit'}
                          </Badge>
                          {cel.actief && (
                            <span className="text-[10px] text-slate-400">
                              {cel.rollen.length ? cel.rollen.join(', ') : 'geen rollen'}
                              {cel.extra > 0 ? ` +${cel.extra}` : ''}
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'wie' && (
        <div className="space-y-3">
          <Select value={gekozen} onValueChange={kiesGebruiker}>
            <SelectTrigger className="w-72" data-testid="wie-gebruiker-select">
              <SelectValue placeholder="Kies een persoon..." />
            </SelectTrigger>
            <SelectContent>
              {gebruikers.map(g => (
                <SelectItem key={g.id || g._id} value={g.id || g._id}>
                  {g.naam} — {g.rol}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {migratie?.gedraaid && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4"
              data-testid="migratie-rapport">
              <div className="px-5 py-3 border-b border-slate-100">
                <p className="text-sm font-medium text-slate-700">Wijzigingen bij de update v18.4</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Uitgevoerd op {migratie.datum ? new Date(migratie.datum).toLocaleDateString('nl-NL', { day: '2-digit', month: 'long', year: 'numeric' }) : 'onbekend'}
                  {' · '}{migratie.templates_aangepast} template{migratie.templates_aangepast === 1 ? '' : 's'} genormaliseerd
                </p>
              </div>
              {migratie.regels.length === 0 ? (
                <p className="px-5 py-4 text-sm text-slate-500" data-testid="migratie-geen-wijziging">
                  Er is geen enkel mailgedrag veranderd — alleen de opslagvorm is gelijkgetrokken.
                </p>
              ) : (
                <div className="divide-y divide-slate-50">
                  {migratie.regels.map((r, i) => (
                    <div key={i} className="px-5 py-2.5" data-testid={`migratie-regel-${i}`}>
                      <p className="text-sm text-slate-700">{r.template} — {r.trigger}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        was: {r.voor} → nu: {r.na}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {wie && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="wie-resultaat">
              <div className="px-5 py-3 border-b border-slate-100">
                <p className="text-sm font-medium text-slate-700">{wie.gebruiker.naam} — {wie.gebruiker.email}</p>
                {wie.voorkeuren.alle_uit && (
                  <p className="text-xs text-red-500 mt-0.5">Deze gebruiker heeft alle e-mail uitgezet</p>
                )}
                {['admin', 'manager'].includes(wie.gebruiker.rol) && (
                  <Button size="sm" variant="outline" className="mt-2"
                    onClick={() => toonVoorbeeld('dagoverzicht', wie.gebruiker.id)}
                    data-testid="wie-voorbeeld-dagoverzicht">
                    Bekijk dagoverzicht voor deze persoon
                  </Button>
                )}
              </div>
              <div className="divide-y divide-slate-50">
                {wie.regels.length === 0 && (
                  <p className="px-5 py-6 text-sm text-slate-400 text-center">Deze persoon ontvangt geen mail</p>
                )}
                {wie.regels.map((r, i) => (
                  <div key={i} className="px-5 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700 truncate">
                        {r.soort}{r.template ? ` — ${r.template}` : ''}
                      </p>
                      <p className="text-xs text-slate-400">{r.reden}</p>
                    </div>
                    <Badge className={r.ontvangt ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}>
                      {r.ontvangt ? 'ontvangt' : 'uitgezet'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'log' && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <Input placeholder="Ontvanger" className="w-52 h-9" value={logFilter.ontvanger}
              onChange={e => setLogFilter(f => ({ ...f, ontvanger: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') laadLog(0, logFilter); }}
              data-testid="log-filter-ontvanger" />
            <Input placeholder="Soort (bv. verlopen)" className="w-52 h-9" value={logFilter.soort}
              onChange={e => setLogFilter(f => ({ ...f, soort: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') laadLog(0, logFilter); }}
              data-testid="log-filter-soort" />
            <Input placeholder="Status" className="w-40 h-9" value={logFilter.status}
              onChange={e => setLogFilter(f => ({ ...f, status: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') laadLog(0, logFilter); }}
              data-testid="log-filter-status" />
            <Button size="sm" variant="outline" onClick={() => laadLog(0, logFilter)} data-testid="log-filter-btn">
              Filteren
            </Button>
            <span className="text-xs text-slate-400">{log.totaal} regels (30 dagen)</span>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="email-log">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  <th className="py-3 px-4 font-semibold text-slate-700">Tijd</th>
                  <th className="py-3 px-4 font-semibold text-slate-700">Soort</th>
                  <th className="py-3 px-4 font-semibold text-slate-700">Aan</th>
                  <th className="py-3 px-4 font-semibold text-slate-700">Onderwerp</th>
                  <th className="py-3 px-4 font-semibold text-slate-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {log.rijen.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="py-2.5 px-4 text-slate-500 whitespace-nowrap">{fmt(r.tijdstip)}</td>
                    <td className="py-2.5 px-4 text-slate-600">{r.soort}</td>
                    <td className="py-2.5 px-4 text-slate-500 text-xs">{(r.to || []).join(', ')}</td>
                    <td className="py-2.5 px-4 text-slate-600 text-xs">{r.onderwerp}</td>
                    <td className="py-2.5 px-4">
                      <Badge className={
                        r.status === 'verzonden' ? 'bg-green-100 text-green-700'
                          : r.status === 'mislukt' ? 'bg-red-100 text-red-700'
                            : 'bg-slate-100 text-slate-500'}>
                        {r.status}{r.reden ? ` — ${r.reden}` : ''}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {log.rijen.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-slate-400">Nog geen verzendregels</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between">
            <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => laadLog(Math.max(0, skip - 25))}>
              Vorige
            </Button>
            <Button size="sm" variant="outline" disabled={skip + 25 >= log.totaal}
              onClick={() => laadLog(skip + 25)} data-testid="log-volgende-btn">
              Volgende
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!voorbeeld} onOpenChange={() => setVoorbeeld(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{voorbeeld?.onderwerp || 'Voorbeeld'}</DialogTitle></DialogHeader>
          <iframe title="mailvoorbeeld" srcDoc={voorbeeld?.html || ''} sandbox=""
            className="w-full h-[60vh] rounded-lg border border-slate-200 bg-white"
            data-testid="voorbeeld-frame" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
